import React, { useCallback, useEffect, useState } from "react";
import "../styles/manageusers.css";
import { Close, Edit, Info, Search } from "../constants";

import {UserField} from '../components/papi-react';
import { get, post } from "../config/api";
import { useDispatch, useSelector } from "react-redux";
import { ROLES } from "../constants/textconstants";
import { API_ENDPOINTS } from "../constants/apiEndpoints";
import { showError } from "../store/slices/formconfig";

interface Unit {
  code: string;
  name: string;
}

interface Squad {
  code: string;
  name: string;
}

interface Territory {
  code: string;
  name: string;
}

interface User {
  id: string | null; // Can be string from API or null for new users
  name: string;
  email: string;
  role: string;
  unitId?: string; // Optional as it's not in the API response
  squadId?: string; // Optional
  territoryId?: string; // Optional
  status: 'Active' | 'Inactive';
  region?: string; // Added for country_code
  lastLogin: string;
 
}

// UserFormData type is used for the modal's internal state
type UserFormData = Omit<User, 'lastLogin'>;

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserFormData | null;
  onSave: (userData: UserFormData) => void;
}

const availableRoles: string[] = ['ISSUER', 'APPROVER', 'Viewer'];
const availableRolesCCMC: string[] = ['ISSUER', 'Viewer'];

const fetchUnits = async (): Promise<Unit[]> => {
  try {
    return await get(`${API_ENDPOINTS.LOCATIONS_UNITS}?countryCode=IN`);
  } catch (error) {
    console.error("Failed to fetch units:", error);
    return [];
  }
};

const fetchSquads = async (unitCode: string): Promise<Squad[]> => {
  try {
    return await get(`${API_ENDPOINTS.LOCATIONS_SQUADS}?countryCode=IN&unitCode=${unitCode}`);
  } catch (error) {
    console.error("Failed to fetch squads:", error);
    return [];
  }
};

const fetchTerritories = async (squadCode: string): Promise<Territory[]> => {
  try {
    return await get(`${API_ENDPOINTS.LOCATIONS_TERRITORIES}?countryCode=IN&squadCode=${squadCode}`);
  } catch (error) {
    console.error("Failed to fetch territories:", error);
    return [];
  }
};


const initialFormData: UserFormData = {
  id: null,
  name: '',
  email: '',
  role: 'ISSUER',
  unitId: '',
  squadId: '',
  territoryId: '',
  status: 'Active',
  
};

const UserModal: React.FC<UserModalProps> = ({ isOpen, onClose, user, onSave }) => {
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [units, setUnits] = useState<Unit[]>([]);
  const [availableSquads, setAvailableSquads] = useState<Squad[]>([]);
  const [availableTerritories, setAvailableTerritories] = useState<Territory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [choosenUser, setChoosenUser] = useState<any>({});
  const [valuefield, setValuefield] = useState<any>({});


  const isEditing = formData.id !== null;
  const userData = useSelector((state: any) => state.formConfig.user);
  // Effect to load initial user data or reset form
  useEffect(() => {
    const loadUnits = async () => {
      const fetchedUnits = await fetchUnits();
      setUnits(fetchedUnits);
    };

    loadUnits();
    if (user && isOpen) {
      // Ensure the role is one of the available radio options, or default if not
      const roleToSet = availableRoles.includes(user.role) ? user.role : (user.role || initialFormData.role);
      setFormData({ ...user, role: roleToSet });
    } else if (isOpen) {
      setFormData(initialFormData);
    }
    setFormErrors({});
  }, [user, isOpen]);

  // Effect for fetching Squads (cascading logic)
  useEffect(() => {
    const loadSquads = async () => {
      if (formData.unitId) {
        setIsLoading(true);
        const squads = await fetchSquads(formData.unitId);
        setAvailableSquads(squads);

        // Check if current squad exists in new list
        const currentSquadExists = squads.some(s => s.code === formData.squadId);
        if (!currentSquadExists) {
          setFormData(prev => ({ ...prev, squadId: '', territoryId: '' }));
          setAvailableTerritories([]);
        }
        setIsLoading(false);
      } else {
        setAvailableSquads([]);
        setAvailableTerritories([]);
        setFormData(prev => ({ ...prev, squadId: '', territoryId: '' }));
      }
    };
    if (isOpen) {
      loadSquads();
    }
  }, [formData.unitId, formData.squadId, isOpen]);

  // Effect for fetching Territories (cascading logic)
  useEffect(() => {
    const loadTerritories = async () => {
      if (formData.squadId) {
        setIsLoading(true);
        const territories = await fetchTerritories(formData.squadId);
        setAvailableTerritories(territories);

        // Check if current territory exists in new list
        const currentTerritoryExists = territories.some(t => t.code === formData.territoryId);
        if (!currentTerritoryExists) {
          setFormData(prev => ({ ...prev, territoryId: '' }));
        }
        setIsLoading(false);
      } else {
        setAvailableTerritories([]);
        setFormData(prev => ({ ...prev, territoryId: '' }));
      }
    };
    if (isOpen) {
      loadTerritories();
    }
  }, [formData.squadId, formData.territoryId, isOpen]);

  // Unified change handler for inputs and selects
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // TypeScript assertion to ensure 'name' is a key of UserFormData
    setFormData(prev => ({ ...prev, [name]: value } as UserFormData));
    
    // Clear error for the field being edited
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const requiredFields: (keyof UserFormData)[] = ['unitId', 'squadId', 'territoryId', 'role', 'status'];

    requiredFields.forEach(field => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        errors[field] = 'This field is mandatory.';
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave({...formData, ...choosenUser});
      onClose();
    } else {
      console.error("Validation Failed:", formErrors);
    }
  };

  const choosenUserChange = (data:any)=>{
    if(Object.keys(data).length>2){
      setChoosenUser(data);
    }
    setValuefield(data)
              
           
              
  }

  if (!isOpen) return null;

  const isSquadDisabled = !formData.unitId;
  const isTerritoryDisabled = !formData.squadId;

  const title = isEditing ? 'Edit User' : 'Add New User';

  return (
    <div className="modal-overlay">
      <div className="modal-content"  style={{width:500}}>
        {/* Modal Header */}
        <div className="modal-header" style={{position:'relative', border:'none'}}>
          <h3 className="modal-title">{title}</h3>
          <button onClick={onClose} className="modal-close-button">
            <img src={Close} height={25} alt="" />
          </button>
        </div>

        {/* Modal Body - Form */}
        <div className="modal-body">
          {Object.keys(formErrors).length > 0 && (
            <div className="error-summary">
              Please correct the mandatory fields marked with an asterisk (<span className="text-red">*</span>).
            </div>
          )}
           
          {/* 1. Name (User Search) */}
          <div className="space-y-1" style={{marginBottom:20}}>
            <label className="label-text">
              Name <span className="required-star">*</span>
            </label>
            <div className={`input-wrapper ${formErrors.name ? 'input-error' : ''}`}>
             <UserField value={valuefield?valuefield.name:null} onChange={choosenUserChange}/>
            </div>
            {formErrors.name && <p className="error-message">{formErrors.name}</p>}
          </div>

          {/* 2, 3, 4. Unit, Squad, Territory (Cascading Dropdowns) */}
          <div className="grid-3-col" style={{marginBottom:20}}>
            
            {/* Unit (Dropdown 1) */}
            <div>
              <label className="label-text">
                Unit <span className="required-star">*</span>
              </label>
              <select
                name="unitId"
                value={formData.unitId}
                onChange={handleChange}
                className={`select-field ${formErrors.unitId ? 'input-error' : ''}`}
              >
                <option value="">Select unit</option>
                {units.map(unit => (
                  <option key={unit.code} value={unit.code}>{unit.name}</option>
                ))}
              </select>
              {formErrors.unitId && <p className="error-message">{formErrors.unitId}</p>}
            </div>

            {/* Squad (Dropdown 2) - Dependent on Unit */}
            <div style={{marginBottom:20}}>
              <label className={`label-text ${isSquadDisabled ? 'text-gray-400' : ''}`}>
                Squad <span className="required-star">*</span>
              </label>
              <select
                name="squadId"
                value={formData.squadId}
                onChange={handleChange}
                disabled={isSquadDisabled || isLoading}
                className={`select-field ${formErrors.squadId ? 'input-error' : ''} ${isSquadDisabled ? 'select-disabled' : ''}`}
              >
                <option value="">{isLoading && !isSquadDisabled ? 'Loading Squads...' : isSquadDisabled ? 'Select unit' : 'Select squad'}</option>
                {availableSquads.map(squad => (
                  <option key={squad.code} value={squad.code}>{squad.name}</option>
                ))}
              </select>
              {formErrors.squadId && <p className="error-message">{formErrors.squadId}</p>}
            </div>

            {/* Territory (Dropdown 3) - Dependent on Squad */}
            <div>
              <label className={`label-text ${isTerritoryDisabled ? 'text-gray-400' : ''}`}>
                Territory <span className="required-star">*</span>
              </label>
              <select
                name="territoryId"
                value={formData.territoryId}
                onChange={handleChange}
                disabled={isTerritoryDisabled || isLoading}
                className={`select-field ${formErrors.territoryId ? 'input-error' : ''} ${isTerritoryDisabled ? 'select-disabled' : ''}`}
              >
                <option value="">{isLoading && !isTerritoryDisabled ? 'Loading Territories...' : isTerritoryDisabled ? 'Select squad' : 'Select territory'}</option>
                {availableTerritories.map(territory => (
                  <option key={territory.code} value={territory.code}>{territory.name}</option>
                ))}
              </select>
              {formErrors.territoryId && <p className="error-message">{formErrors.territoryId}</p>}
            </div>
            
          </div>

          {/* 5. Role Selection (Radio Buttons) */}
          <div className="space-y-2" style={{marginBottom:20}}>
            <label className="radio-group-label">
              Role <span className="required-star">*</span>
            </label>
            <div className="radio-group">
              {userData.role !== ROLES.APPROVER && availableRoles.map(roleOption => (
                <div key={roleOption} className="radio-option">
                  <input
                    type="radio"
                    id={`role-${roleOption}`}
                    name="role"
                    value={roleOption}
                    checked={formData.role === roleOption}
                    onChange={handleChange}
                    className="radio-input"
                  />
                  <label htmlFor={`role-${roleOption}`} className="radio-label">
                    {roleOption}
                  </label>
                 <img src={Info} height={14} width={14} className="role-info-icon" title={roleOption === 'ISSUER' ?`View/Approve/Reject/Cancel Certificate Requests`:(roleOption==='Viewer'? 'View Issued Certificates': 'View/Cancel Issued Certificates') } alt="info" />
                </div>
              ))}
               {userData.role === ROLES.APPROVER && availableRolesCCMC.map(roleOption => (
                <div key={roleOption} className="radio-option">
                  <input
                    type="radio"
                    id={`role-${roleOption}`}
                    name="role"
                    value={roleOption}
                    checked={formData.role === roleOption}
                    onChange={handleChange}
                    className="radio-input"
                  />
                  <label htmlFor={`role-${roleOption}`} className="radio-label">
                    {roleOption}
                  </label>
                 <img src={Info} height={14} width={14} className="role-info-icon" title={roleOption === 'ISSUER' ?`View/Approve/Reject/Cancel Certificate Requests`:(roleOption==='Viewer'? 'View Issued Certificates': 'View/Cancel Issued Certificates') } alt="info" />
                </div>
              ))}
            </div>
            {formErrors.role && <p className="error-message">{formErrors.role}</p>}

            {/* Role Info Note */}
            <div className="info-note">
              Note: License manager and admin roles need to be added manually. Please contact the system administrator to assign this role.
            </div>
          </div>

          {/* 6. Status Selection (Radio Buttons) */}
          <div className="space-y-2">
            <label className="radio-group-label">
              Status <span className="required-star">*</span>
            </label>
            <div className="radio-group">
              {['Active', 'Inactive'].map(statusOption => (
                <div key={statusOption} className="radio-option">
                  <input
                    type="radio"
                    id={`status-${statusOption}`}
                    name="status"
                    value={statusOption}
                    checked={formData.status === statusOption}
                    onChange={handleChange}
                    className="radio-input"
                  />
                  <label htmlFor={`status-${statusOption}`} className="radio-label">
                    {statusOption}
                  </label>
                </div>
              ))}
            </div>
            {formErrors.status && <p className="error-message">{formErrors.status}</p>}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button
            onClick={onClose}
            className="cancel-button"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="save-button"
          >
            {/* <Save size={18} /> */}
            {isEditing ? 'Update User' : 'Save User'}
            {isLoading && <span className="spinner"></span>}
          </button>
        </div>
      </div>
    </div>
  );
};

const ManageUsers = () => {
   const [users, setUsers] = useState<User[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<UserFormData | null>(null);
  const dispatch = useDispatch();


  const fetchUsersList = async () => {
    try {
      const response = await get(API_ENDPOINTS.USERS); 
      if (response && Array.isArray(response.items)) {
        const mappedUsers: User[] = response.items.map((apiUser: any) => ({
          id: apiUser.user_id,
          name: apiUser.username,
          email: apiUser.email,
          role: apiUser.role,
          status: apiUser.active ? 'Active' : 'Inactive',
          region: apiUser.country_code,
          lastLogin: apiUser.last_login ? new Date(apiUser.last_login).toLocaleDateString() : 'Never',
          // Set default empty values for fields not in the API response
          unitId: '',
          squadId: '',
          territoryId: '',
        }));
        setUsers(mappedUsers); 
        
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      
    }
  }

  useEffect(()=>{
    fetchUsersList()
  },[])

  const handleOpenAdd = () => {
    setEditingUser(initialFormData);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (user: User) => {
    // Cast User to UserFormData for the modal state
    const userFormData: UserFormData = {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      unitId: user.unitId || '',
      squadId: user.squadId || '',
      territoryId: user.territoryId || '',
      status: user.status,
    };
    setEditingUser(userFormData);
    setIsModalOpen(true);
  };

  const handleSaveUser = async (userData: UserFormData) => {

    try {
       let dataToSend:any = {
            "user_id": userData.id,
            "country_code": "IN",
            "role": userData.role,
            "username": userData.name,
            "email": userData.email?userData.email:'test@test.com',
            "active": true,
            "unit": userData.unitId,
            "squad": userData.squadId,
            "territory": userData.territoryId,
      };

  
      let response = await post(API_ENDPOINTS.USERS, dataToSend);
     if(response){
    
     }else{
       dispatch(showError({ message: "User Creation Failed. Please try again.", visible: true }))
     }

    } catch (error) {
      dispatch(showError({ message: "User Creation Failed. Please try again.", visible: true }))
      console.error("Failed to save user:", error);
    }

  };

  const renderStatus = (status: 'Active' | 'Inactive') => {
    const baseClasses = "status-badge";
    if (status === 'Active') {
      return <span className={`${baseClasses} status-active`}>{status}</span>;
    }
    return <span className={`${baseClasses} status-inactive`}>{status}</span>;
  };
  return (
    <>
        {users.length>0 ? <div style={{ background: 'rgb(236, 239, 241)', padding: 30, paddingTop: 100, paddingBottom: 80 }}>
        <div className="manage-users" >

          <div className="issued-certificates-header">
            <h3>Manage Users</h3>
            <button className="assign-button" onClick={handleOpenAdd}>+ Add New User</button>

          </div>



          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Role(s)</th>
                <th>Region</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, idx) => (
                <tr key={idx} >
                  <td className="userrow">
                    <strong>{user.name}</strong>
                    <br />
                    <span className="email">{user.email}</span>
                  </td>
                  <td className="userrow">{user.role}</td>
                  <td className="userrow">{user.region || 'N/A'}</td>
                  <td className="userrow">
                    <span className={`status ${user.status.toLowerCase()}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="userrow">{user.lastLogin}</td>
                  <td className="userrow">

                    <img src={Edit} onClick={() => handleOpenEdit(user)} className="edit-icon" />

                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> : <div style={{display:'flex',justifyContent: 'center', alignItems:'center', height:'100vh', flexDirection:'column'}}>
        
        <span style={{display:'block', fontSize:16, color:'rgba(0, 0, 0, 0.87)', fontWeight:'400', marginBottom:5}}>No Users Added</span>
        <span style={{display:'block', fontSize:16, color:'rgba(0, 0, 0, 0.60)', fontWeight:'400', marginBottom:24}}>You haven't added any users yet. Click below to add users.</span>
               <button className="assign-button" onClick={handleOpenAdd}>+ Add New User</button>
        </div>}
      
     
      <UserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        user={editingUser}
        onSave={handleSaveUser}
      />
      </>
  );
};

export default ManageUsers;
